import * as React from "react";
import type {JSONCensusEquipment} from "../data/models/JSONCensusEquipment";
import {useCensusEntriesList} from "../data/hooks/CecnsusCoolerControllerHooks";
import {removeEntriesToCensus} from "../data/controllers/CensusCoolerController";
import {JSONCensus} from "../data/models/JSONCensus";
import {isFirstStepCensus, isProcessingCensus} from "../utils";

export const enum PAGES {
    DEFINE_EQUIPMENT,
    ELABORATION,
}

interface DefineEquipmentPageProviderContextType {
    equipmentList: JSONCensusEquipment[];
    removeAllEquipment: () => Promise<void>;
    removeEquipment: (equipment: JSONCensusEquipment) => Promise<JSONCensus>;
    loadCensusEntriesPage: (startIndex: number) => Promise<void>;
    isFetching: boolean;
    isNextPageLoading: boolean;
    isRevalidatingFirstPage: boolean;
    totalCount: number;
    pageSize: number;
    hasNextPage: boolean;
    isFirstPage: boolean;
    refreshEquipmentList: () => Promise<void>;
    currentPage: PAGES;
    updateCurrentPage: (page: PAGES) => void;
    fetchFirstEntriesList: () => boolean;
}

export const DefineEquipmentPageProviderContext =
    React.createContext<DefineEquipmentPageProviderContextType>({} as any);

type DefineEquipmentPageProviderProps = {
    census: JSONCensus;
    isFromExistingCensus?: boolean;
};

export const DefineEquipmentPageProvider: React.FC<
    DefineEquipmentPageProviderProps
> = ({children, census, isFromExistingCensus}) => {
    const [equipmentList, setEquipmentList] =
        React.useState<JSONCensusEquipment[]>();
    const [isFetching, setIsFetching] = React.useState<boolean>(true);
    const [isNextPageLoading, setIsNextPageLoading] =
        React.useState<boolean>(false);
    const [hasNextPage, setHasNextPage] = React.useState<boolean>(true);
    const [isFirstPage, setIsFirstPage] = React.useState<boolean>(true);
    const [shouldFetch, setShouldFetch] = React.useState<boolean>(false);
    const [pageSize, setPageSize] = React.useState<number>(0);
    const [totalCount, setTotalCount] = React.useState<number>(0);
    const [currentPage, setCurrentPage] = React.useState<PAGES>(
        PAGES.DEFINE_EQUIPMENT,
    );

    const censusGuid = census?.guid;

    const censusListRequestParams: any = React.useMemo(
        () => ({
            guid: censusGuid,
            page: 0,
            size: 20,
            shouldFetch: shouldFetch,
        }),
        [censusGuid, shouldFetch],
    );

    const censusEntriesList = useCensusEntriesList(censusListRequestParams);

    const isRevalidatingFirstPage = censusEntriesList?.isRevalidatingFirstPage;

    const fetchFirstEntriesList = () => {
        if (!shouldFetch) {
            setShouldFetch(true);
            return true;
        }
        return false;
    };

    const fetchEntriesList = () => {
        if (!shouldFetch) {
            fetchFirstEntriesList()
        } else {
            censusEntriesList.mutate()
        }
    }

    const updateCurrentPage = (page: PAGES) => {
        setCurrentPage(page);
    };

    const removeAllEquipment = async () => {
        await removeEntriesToCensus(censusGuid, undefined, true);
        await refreshEquipmentList();
    };

    const removeEquipment = async (jsonCensusEquipment: JSONCensusEquipment) => {
        return removeEntriesToCensus(censusGuid, [jsonCensusEquipment.guid]);
    };

    const refreshEquipmentList = (): Promise<void> => {
        return censusEntriesList?.mutate();
    };

    const loadCensusEntriesPage = (index: number): Promise<void> => {
        return censusEntriesList?.handleOnLoadNextPage(index);
    };

    React.useEffect(() => {
        if (
            censusEntriesList?.flattenedData &&
            !censusEntriesList?.isFetching &&
            !censusEntriesList?.isNextPageLoading &&
            !isRevalidatingFirstPage
        ) {
            setEquipmentList(censusEntriesList?.flattenedData);
            setIsFirstPage(censusEntriesList?.lastPageInfo?.first);
            setTotalCount(censusEntriesList?.lastPageInfo?.totalElements);
            setPageSize(censusEntriesList?.lastPageInfo?.pageable.pageSize);
            setHasNextPage(censusEntriesList?.hasNextPage);
        }
    }, [
        censusEntriesList?.isFetching,
        censusEntriesList?.isNextPageLoading,
        isRevalidatingFirstPage,
    ]);

    React.useEffect(() => {
        setIsNextPageLoading(censusEntriesList?.isNextPageLoading);
        setIsFetching(censusEntriesList?.isFetching);
    }, [censusEntriesList?.isFetching, censusEntriesList?.isNextPageLoading]);

    React.useEffect(() => {
        if (isFromExistingCensus) {
            if (isFirstStepCensus(census)) {
                fetchEntriesList();
            }
            if (isProcessingCensus(census)) {
                setCurrentPage(PAGES.ELABORATION);
            }
        }
    }, [census, isFromExistingCensus, shouldFetch]);

    return (
        <DefineEquipmentPageProviderContext.Provider
            value={{
                equipmentList,
                removeAllEquipment,
                removeEquipment,
                isFetching,
                isNextPageLoading,
                isRevalidatingFirstPage,
                loadCensusEntriesPage,
                totalCount,
                hasNextPage,
                pageSize,
                isFirstPage,
                refreshEquipmentList,
                currentPage,
                updateCurrentPage,
                fetchFirstEntriesList,
            }}
        >
            {children}
        </DefineEquipmentPageProviderContext.Provider>
    );
};
